import { useEffect, useId, useRef, useState, type ComponentPropsWithRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, FileText, X } from 'lucide-react';
import type { LibraryDocument } from '@worki/shared';
import { AnchoredMenu } from '../../components/AnchoredMenu.js';
import { Button, Input, Spinner } from '../../components/ui.js';
import { cn } from '../../lib/cn.js';

/**
 * §7 (05.10) — rows drawn when the list opens, and how many more each time the
 * end of what is drawn comes into view.
 *
 * It paces the drawing and not the fetching: the screen already holds the whole
 * shelf, fetched a page after another until none is left, so a business with
 * hundreds of documents opens the list as quickly as one with ten.
 */
export const PAGE = 20;

/**
 * The tallest the list is: some ten rows on a computer and seven on a phone, so
 * the form under it still shows. The popup makes it shorter where the screen
 * has less room, which on a phone with the keyboard up is often.
 */
const LIST_HEIGHT = 400;

/**
 * The filed documents whose name carries what was typed.
 *
 * Anywhere in the name rather than only at its start: a document filed as
 * "טופס בטיחות באתר" is looked for by "בטיחות", which is the word that tells it
 * apart from the other forms. An empty box searches for nothing and so offers
 * the shelf as it stands.
 *
 * Every match, not the first few: §7 — the list kept eight and threw the rest
 * away, so a ninth document could not be reached from this screen at all.
 */
export function matchDocuments(documents: LibraryDocument[], typed: string): LibraryDocument[] {
  const needle = typed.trim().toLocaleLowerCase();
  return needle
    ? documents.filter((document) => document.title.toLocaleLowerCase().includes(needle))
    : documents;
}

/**
 * The rows drawn: the first `shown` of the matches, and on as far as the row the
 * arrow keys have lit, which can be past them.
 */
export function rowsToDraw(matches: LibraryDocument[], shown: number, active: number): LibraryDocument[] {
  return matches.slice(0, Math.max(shown, active + 1));
}

/** 1.23 — one more id on the end, or the id taken out: the order is the order they were ticked. */
export function toggleTicked(ticked: string[], id: string): string[] {
  return ticked.includes(id) ? ticked.filter((one) => one !== id) : [...ticked, id];
}

/**
 * The ticked documents, in the order they were ticked: the pages of what they
 * become follow it. One the shelf no longer holds (deleted while the list was
 * open) is left out, and so is not fetched.
 */
export function tickedDocuments(documents: LibraryDocument[], ticked: string[]): LibraryDocument[] {
  return ticked.flatMap((id) => documents.find((document) => document.id === id) ?? []);
}

interface DocumentTitleFieldProps {
  id?: string;
  value: string;
  onChange: (title: string) => void;
  /** Everything the business has filed, already fetched by the screen. */
  documents: LibraryDocument[];
  /** A filed document was chosen: its file is fetched and its name taken. */
  onPick: (document: LibraryDocument) => void;
  /**
   * 1.23 — several filed documents were ticked and added together, in the order
   * they were ticked. Given, every row has a box and the list ends in a button;
   * left out, a row can only be picked, as before.
   */
  onPickMany?: (documents: LibraryDocument[]) => void;
  /**
   * 1.29 — given while a filed document is what was picked: the X lets the pick
   * go as well as the name. §7 — the X is there whenever the box holds a name;
   * without this it only empties the box.
   */
  onCancel?: () => void;
  /**
   * §7, option ד — a computer's way: the list opens from a labelled button
   * inside the box, and the X is a bare icon between the name and that button.
   * Left out, the arrow and the X sit in the end of the box as they always
   * have, which is what a phone and the app keep.
   */
  desktop?: boolean;
  /** §7 — the shelf is still being fetched: a spinner where the arrow will be, rather than an arrow that cannot be pressed. */
  loading?: boolean;
  /** §7 — given when the shelf could not be fetched: a line under the box that says so, and a button that asks again. */
  onRetry?: () => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * SIG — the document's name, and the pick of a filed document, in one field.
 *
 * These used to be two controls asking one question: a title box, and a
 * `<select>` of "my documents" under it. A sender opening this screen is
 * thinking of a document by its name, so the name box is where the shelf
 * belongs — typed into freely for a document that has none yet, searched by
 * whatever was typed, and opened whole by the arrow at its end.
 *
 * The same `AnchoredMenu` every other dropdown here rides, so it portals out of
 * the card it sits in and cannot be clipped by it. Unlike theirs, this one
 * scrolls (§7): it can hold the whole shelf.
 */
export function DocumentTitleField({
  id,
  value,
  onChange,
  documents,
  onPick,
  onPickMany,
  onCancel,
  desktop = false,
  loading = false,
  onRetry,
  placeholder,
  disabled,
}: DocumentTitleFieldProps) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [width, setWidth] = useState(0);
  /** 1.23 — the ids ticked in the list, in the order they were ticked. */
  const [ticked, setTicked] = useState<string[]>([]);
  /* Opened by the arrow, the list is the whole shelf; typing searches it. With
     a document already picked its name is in the box, and searching by it left
     a list of that one document, so the arrow could not reach any other. */
  const [browsing, setBrowsing] = useState(false);
  /** §7 — how many of the matches are drawn so far. */
  const [shown, setShown] = useState(PAGE);
  /** Set by the arrow keys, so that only they scroll the list to the lit row and a pointer resting on one does not. */
  const steered = useRef(false);

  const matches = matchDocuments(documents, browsing ? '' : value);
  const drawn = rowsToDraw(matches, shown, active);
  const showList = open && matches.length > 0;
  /* Kept across what is typed, so documents found by different searches can be
     added together; the count on the button is all of them. */
  const tickedOnShelf = tickedDocuments(documents, ticked);
  /* §7 — whenever there is something for it to take out: a name, or a pick. */
  const clearable = value !== '' || Boolean(onCancel);
  const clearLabel = t(onCancel ? 'signing.fileCancel' : 'signing.documentNameClear');

  useEffect(() => setActive(0), [value]);

  useEffect(() => {
    if (open) setWidth(wrapRef.current?.getBoundingClientRect().width ?? 0);
  }, [open, matches.length]);

  // The ticks belong to one opening of the list: closed, by a pick, by Escape or
  // by pressing elsewhere, it opens again with none.
  useEffect(() => setTicked((was) => (open || was.length === 0 ? was : [])), [open]);

  // A new search, or the list opened again, starts from its top with one page
  // drawn. The popup stays open while the name is typed, so it would otherwise
  // keep its place in the last search's rows. The list's parent is the popup,
  // which is what scrolls.
  useEffect(() => {
    setShown(PAGE);
    document.getElementById(listId)?.parentElement?.scrollTo({ top: 0 });
  }, [value, browsing, open, listId]);

  useEffect(() => {
    if (!steered.current) return;
    steered.current = false;
    document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active, listId]);

  const choose = (document: LibraryDocument) => {
    onPick(document);
    setOpen(false);
    inputRef.current?.focus();
  };

  const addTicked = () => {
    onPickMany?.(tickedOnShelf);
    setOpen(false);
    inputRef.current?.focus();
  };

  /* The arrow is a button of its own rather than the whole field: the box is
     typed into far more often than the shelf is browsed, and a field that opens
     a list on every focus covers what is being typed. */
  const browse = () => {
    setOpen((was) => !was);
    setBrowsing(true);
    inputRef.current?.focus();
  };

  /**
   * §7 — the X empties the box, and with a filed document picked lets the pick
   * go too (1.29). On a computer the next thing is another name, so the box has
   * the keyboard back; a phone is left with its keyboard down, as the X found it
   * after a pick off the shelf.
   */
  const clear = () => {
    onChange('');
    onCancel?.();
    setOpen(false);
    if (desktop) inputRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (event.key === 'Escape') {
      // Stops here: a document-level Escape closes the dialog this screen can
      // sit inside, and dismissing a suggestion list must not take it down too.
      event.stopPropagation();
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      steered.current = true;
      setActive((current) =>
        Math.min(Math.max(current + (event.key === 'ArrowDown' ? 1 : -1), 0), matches.length - 1),
      );
      return;
    }
    if (event.key === 'Enter' && matches[active]) {
      // Only while a row is highlighted, so Enter still submits for anyone
      // naming a document that was never filed.
      event.preventDefault();
      choose(matches[active]);
    }
  };

  const box: ComponentPropsWithRef<'input'> = {
    ref: inputRef,
    id,
    role: 'combobox',
    'aria-expanded': showList,
    'aria-controls': showList ? listId : undefined,
    'aria-activedescendant': showList && matches[active] ? `${listId}-${active}` : undefined,
    'aria-autocomplete': 'list',
    autoComplete: 'off',
    maxLength: 160,
    disabled,
    placeholder,
    value,
    onChange: (event) => {
      onChange(event.target.value);
      setBrowsing(false);
      // Typing searches; it does not pick. The list follows the keystrokes
      // rather than waiting for the arrow to be found.
      setOpen(true);
    },
    onKeyDown,
  };

  const shelfButton = {
    type: 'button' as const,
    disabled: disabled || documents.length === 0,
    onClick: browse,
    'aria-label': t('signing.pickFiledDocument'),
    title: t('signing.pickFiledDocument'),
    'aria-busy': loading || undefined,
  };

  const chevron = loading ? (
    <Spinner />
  ) : (
    <ChevronDown className={cn('h-4 w-4 transition-transform', showList && 'rotate-180')} />
  );

  const menu = (
    <AnchoredMenu
      open={showList}
      onClose={() => setOpen(false)}
      anchorRef={wrapRef}
      width={Math.max(width, 200)}
      maxHeight={LIST_HEIGHT}
    >
      <ShelfList
        id={listId}
        documents={drawn}
        value={value}
        active={active}
        onActive={setActive}
        onPick={choose}
        onMore={drawn.length < matches.length ? () => setShown((was) => was + PAGE) : undefined}
        many={
          onPickMany
            ? {
                ticked: tickedOnShelf,
                onToggle: (document) => setTicked((was) => toggleTicked(was, document.id)),
                onAdd: addTicked,
              }
            : undefined
        }
      />
    </AnchoredMenu>
  );

  return (
    <>
      {desktop ? (
        /* §7, option ד — one box, as tall and as filled as the plain one: the
           name across most of it, then the X, then "המסמכים שלי ▾" in a frame
           of its own at its end. The box is drawn here rather than by the input,
           so the button sits inside it, and it lights up while anything in it
           has the focus, as the input does on its own. */
        <div
          ref={wrapRef}
          className={cn(
            'relative flex min-h-12 min-w-0 items-center gap-1 rounded-lg border border-transparent bg-muted-foreground/10 pe-2 transition-colors',
            'focus-within:bg-muted-foreground/[0.16] dark:border-input dark:bg-muted-foreground/[0.16] dark:focus-within:bg-muted-foreground/[0.22]',
            disabled && 'opacity-60',
          )}
        >
          <input
            {...box}
            className="min-w-0 flex-1 self-stretch rounded-s-lg bg-transparent ps-3 text-base outline-none placeholder:text-muted-foreground"
          />
          {/* The bare icon the spec asks for: no frame and no fill, as tall as the
              button beside it and the colour of its words. */}
          {clearable && (
            <button
              type="button"
              disabled={disabled}
              onClick={clear}
              aria-label={clearLabel}
              title={clearLabel}
              className="grid h-8 w-8 shrink-0 place-items-center text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            {...shelfButton}
            className={cn(
              'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-muted-foreground/30 bg-card px-2.5',
              'text-sm text-muted-foreground hover:text-foreground transition-colors',
              !loading && 'disabled:opacity-40',
            )}
          >
            {t('signing.myDocuments')}
            {chevron}
          </button>
          {menu}
        </div>
      ) : (
        <div ref={wrapRef} className="relative min-w-0">
          {/* Room for the arrow, and for the X beside it when there is one. */}
          <Input {...box} className={cn('min-h-12 text-base', clearable ? 'pe-[4.5rem]' : 'pe-10')} />
          <button
            {...shelfButton}
            tabIndex={-1}
            className={cn(
              'absolute inset-y-0 end-0 w-10 grid place-items-center rounded-e-xl',
              'text-muted-foreground hover:text-foreground transition-colors',
              // A spinner is not faded: it is the arrow on its way, not one that is off.
              !loading && 'disabled:opacity-30',
            )}
          >
            {chevron}
          </button>
          {/* 1.29 — beside the arrow, on its inner side. It is tabbable, unlike
              the arrow: the arrow has the keyboard on the box itself, and nothing
              else can take the pick back. */}
          {clearable && (
            <button
              type="button"
              disabled={disabled}
              onClick={clear}
              aria-label={clearLabel}
              title={clearLabel}
              className="absolute inset-y-0 end-10 w-8 grid place-items-center text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {menu}
        </div>
      )}
      {onRetry && (
        <div role="alert" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-destructive">
          <span>{t('signing.libraryLoadFailed')}</span>
          <Button type="button" variant="ghost" size="sm" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        </div>
      )}
    </>
  );
}

interface ShelfListProps {
  id: string;
  /** The rows to draw: already searched, and as many as are drawn so far. */
  documents: LibraryDocument[];
  /** What is typed in the name box: the row it names is the current pick. */
  value: string;
  active: number;
  onActive: (index: number) => void;
  /** A plain press on a row: that one document, now. */
  onPick: (document: LibraryDocument) => void;
  /** §7 — given while the search has rows not drawn yet: asked for them as the last ones drawn come into view. */
  onMore?: () => void;
  /** 1.23 — given, a box on every row and a button at the foot; left out, neither. */
  many?: {
    ticked: LibraryDocument[];
    onToggle: (document: LibraryDocument) => void;
    onAdd: () => void;
  };
}

/**
 * The rows of "my documents" under the name box, and 1.23's way of taking
 * several of them.
 *
 * A component of its own because the popup it is drawn into is a portal, which
 * a server render cannot enter: the rows are what a test has to be able to look
 * at, and it can only look at what is not behind one.
 */
export function ShelfList({ id, documents, value, active, onActive, onPick, onMore, many }: ShelfListProps) {
  const { t } = useTranslation();

  return (
    <>
      <div id={id} role="listbox" aria-label={t('signing.pickFiledDocument')} className="relative p-1">
        {documents.map((document, index) => (
          <div
            key={document.id}
            id={`${id}-${index}`}
            role="option"
            aria-selected={document.title === value}
            onMouseEnter={() => onActive(index)}
            className={cn(
              'flex items-stretch min-h-11 sm:min-h-9 rounded-lg text-sm cursor-pointer',
              // A row the arrow keys bring into view clears the foot, which stays
              // over the bottom of the list as it scrolls.
              many && 'scroll-mb-14',
              index === active && 'bg-muted',
              document.title === value && 'font-medium text-primary',
            )}
          >
            {/* 1.23 — the box is a strip the height of the row, not the sixteen
                pixels of the box itself: on a phone a miss lands on the row, and
                the row picks. It is the start of the row, so it is on the right
                in Hebrew. The box only shows; the press is the strip's. */}
            {many && (
              <span
                onMouseDown={(event) => {
                  event.preventDefault();
                  many.onToggle(document);
                }}
                className="w-10 shrink-0 grid place-items-center"
              >
                <input
                  type="checkbox"
                  tabIndex={-1}
                  checked={many.ticked.some((one) => one.id === document.id)}
                  onChange={() => many.onToggle(document)}
                  aria-label={document.title}
                  className="h-4 w-4 accent-primary pointer-events-none"
                />
              </span>
            )}
            {/* `onMouseDown` and not `onClick`: the input blurs first
                otherwise, and the blur closes the list out from under the
                pointer before the click can land. */}
            <span
              onMouseDown={(event) => {
                event.preventDefault();
                onPick(document);
              }}
              className="flex min-w-0 flex-1 items-center gap-2 px-2.5"
            >
              <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate">{document.title}</span>
            </span>
          </div>
        ))}
        {/* Keyed by the rows drawn, so each page drawn is watched afresh: one
            that still leaves the end in view asks for the next straight away. */}
        {onMore && <MoreRows key={documents.length} onMore={onMore} />}
      </div>
      {/* 1.23 — there from the first, a line that says what the boxes are for
          until one is ticked and the button after: the popup is placed once,
          when it opens, so a button that arrived below the last row would fall
          off the end of a short screen, and the rows would jump to make room.
          §7 — it stays at the bottom of the popup while the rows scroll under it. */}
      {many && (
        <div className="border-t border-border p-1 sticky bottom-0 bg-card">
          {many.ticked.length > 0 ? (
            <Button
              type="button"
              variant="primary"
              block
              className="min-h-11 sm:min-h-9"
              /* The focus stays in the name box, as it does for the rows. */
              onMouseDown={(event) => event.preventDefault()}
              onClick={many.onAdd}
            >
              {t('signing.pickFiledAdd', { count: many.ticked.length })}
            </Button>
          ) : (
            <p className="flex min-h-11 sm:min-h-9 items-center px-2.5 text-xs text-muted-foreground">
              {t('signing.pickFiledManyHint')}
            </p>
          )}
        </div>
      )}
    </>
  );
}

/**
 * §7 — the last rows drawn, watched: as they come into view the next page is
 * drawn under them, so the list goes on for as long as it is scrolled.
 *
 * It lies over the last few rows rather than after them, so the next page is
 * asked for before the end is reached, and it takes no room and no presses.
 */
function MoreRows({ onMore }: { onMore: () => void }) {
  const ref = useRef<HTMLSpanElement>(null);
  /* The latest, without watching again every time the field draws: the field
     hands a new function each time. */
  const more = useRef(onMore);
  more.current = onMore;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) more.current();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return <span ref={ref} aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-40" />;
}
